"use client";

import { useQuery } from "convex/react";
import { api } from "@convex/_generated/api";
import { useEffect, useRef } from "react";
import { HoverPrefetchLink } from "@/components/ui/hover-prefetch-link";
import { toDiscoveryProduct } from "@/components/listing/browse/marketplace-listing";
import { useMediaMatches } from "@/hooks/use-mobile";
import { ProductTypeIcon } from "@/lib/icons/product-icons";
import { cn } from "@/lib/utils";
import { listingItem } from "./landing-listing-item";

const LISTING_COUNT = 24;
const ROWS = 3;
const SECONDS_PER_ITEM = 5;
const SKELETON_ROWS = Array.from({ length: ROWS }, (_, index) => index);

type Item = ReturnType<typeof listingItem>;

function pauseWhileEngaged(track: HTMLElement, animation: Animation): () => void {
  const pause = () => animation.pause();
  const play = () => animation.play();
  track.addEventListener("pointerenter", pause);
  track.addEventListener("pointerleave", play);
  track.addEventListener("focusin", pause);
  track.addEventListener("focusout", play);
  return () => {
    track.removeEventListener("pointerenter", pause);
    track.removeEventListener("pointerleave", play);
    track.removeEventListener("focusin", pause);
    track.removeEventListener("focusout", play);
  };
}

function MarqueeRow({ items, reverse }: { items: Item[]; reverse: boolean }) {
  const trackRef = useRef<HTMLDivElement>(null);
  const prefersReducedMotion = useMediaMatches("(prefers-reduced-motion: reduce)");
  useEffect(() => {
    const track = trackRef.current;
    if (!track || prefersReducedMotion) return;
    const animation = track.animate([{ transform: "translateX(0)" }, { transform: "translateX(-50%)" }], {
      duration: items.length * SECONDS_PER_ITEM * 1000,
      iterations: Infinity,
      direction: reverse ? "reverse" : "normal",
    });
    const release = pauseWhileEngaged(track, animation);
    return () => {
      release();
      animation.cancel();
    };
  }, [items.length, prefersReducedMotion, reverse]);
  return (
    <div className="overflow-hidden">
      <div ref={trackRef} className="flex w-max gap-3 pr-3">
        {[...items, ...items].map((item, index) => (
          <HoverPrefetchLink
            key={`${item.href}-${index}`}
            href={item.href}
            aria-hidden={index >= items.length}
            tabIndex={index >= items.length ? -1 : undefined}
            className="flex items-center gap-3 rounded-2xl border border-border bg-surface py-2.5 pl-2.5 pr-5 outline-none transition-colors hover:border-brand focus-visible:ring-3 focus-visible:ring-ring"
          >
            <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-brand-tint">
              <ProductTypeIcon slug={item.productTypeSlug ?? "pallets"} size={24} className="size-6" />
            </span>
            <span className="flex flex-col">
              <span className="max-w-[260px] truncate text-sm font-semibold">{item.name}</span>
              <span className="text-xs text-fg-muted">{item.location.split(" · ")[0]}</span>
            </span>
            <span className="ml-2 text-sm font-semibold tabular-nums text-brand-strong">
              {item.unitPrice ? `$${item.unitPrice}` : (item.freeLabel ?? "")}
            </span>
          </HoverPrefetchLink>
        ))}
      </div>
    </div>
  );
}

function splitRows(items: Item[]): Item[][] {
  const rows: Item[][] = Array.from({ length: ROWS }, () => []);
  items.forEach((item, index) => rows[index % ROWS]?.push(item));
  return rows.filter((row) => row.length >= 4);
}

export function LandingClosingMarquee({ className }: { className?: string }) {
  const page = useQuery(api.products.marketplaceQueries.listMarketplacePage, {
    page: 1,
    perPage: LISTING_COUNT,
    inStock: true,
    sortBy: "newest",
    intent: "sell",
  });
  const rows = page ? splitRows(page.listings.map((listing) => listingItem(toDiscoveryProduct(listing)))) : null;
  return (
    <div
      className={cn(
        "flex flex-col gap-3 [mask-image:linear-gradient(to_right,transparent,black_56px,black_calc(100%-56px),transparent)]",
        className,
      )}
    >
      {rows
        ? rows.map((row, index) => <MarqueeRow key={index} items={row} reverse={index % 2 === 1} />)
        : SKELETON_ROWS.map((index) => (
            <div key={index} aria-hidden className="h-[62px] animate-pulse rounded-2xl bg-skeleton motion-reduce:animate-none" />
          ))}
    </div>
  );
}
